import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-reimagined/components/layout';
import ProgressBar from 'tio-reimagined/components/progress-bar';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import Button from 'tio-reimagined/components/button';
import FilterTabs from 'tio-reimagined/components/filter-tabs';
import { eq } from 'tio-ui/utilities';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import PageHero from 'tio-reimagined/components/page-hero';
import StatCard from 'tio-reimagined/components/stat-card';
import {
  PhLightbulb,
  PhPlus,
  PhPencil,
  PhCheckCircle,
  PhWarning,
  PhTrendUp,
  PhBriefcase,
  PhGraduationCap,
  PhCertificate,
  PhStar,
  PhSparkle,
  PhArrowRight,
  PhChartBar,
  PhTarget,
} from 'ember-phosphor-icons';
import type {
  CareerSummary,
  EmployeeSkill,
  SkillGap,
} from 'tio-employee/services/career-pathways-api';

interface CareerPathwaysSkillsIndexSignature {
  Args: {
    model: {
      skills: EmployeeSkill[];
      summary: CareerSummary;
      skillGaps: SkillGap[];
    };
  };
}

export default class CareerPathwaysSkillsIndex extends Component<CareerPathwaysSkillsIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;
  @tracked selectedCategory = 'all';
  @tracked isAddingSkill = false;

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get dataSource(): DataSource {
    // Skills data is entirely demo/placeholder — no backend API yet
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  get skills() {
    return this.args.model.skills;
  }

  get summary() {
    return this.args.model.summary;
  }

  get skillGaps() {
    return this.args.model.skillGaps;
  }

  get verifiedSkills() {
    return this.skills.filter((skill) => skill.source === 'verified');
  }

  get inProgressSkills() {
    return this.skills.filter((skill) => skill.source === 'in-progress');
  }

  get selfReportedSkills() {
    return this.skills.filter((skill) => skill.source === 'self-reported');
  }

  get skillCategories() {
    return [
      { id: 'all', name: 'All Skills', count: this.summary.skillsTracked },
      {
        id: 'technical',
        name: 'Technical',
        count: this.skills.filter((skill) => skill.category === 'technical').length,
      },
      {
        id: 'soft',
        name: 'Soft Skills',
        count: this.skills.filter((skill) => skill.category === 'soft').length,
      },
      { id: 'gaps', name: 'Skill Gaps', count: this.skillGaps.length },
    ];
  }

  get categoryTabs() {
    return this.skillCategories.map((category) => ({
      id: category.id,
      label: category.name,
      count: category.count,
    }));
  }

  formatSkillLevel = (level: EmployeeSkill['level']) => {
    return level
      .split('-')
      .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' ');
  };

  formatDate = (dateValue?: string) => {
    if (!dateValue) return 'TBD';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(new Date(dateValue));
  };

  setCategory = (categoryId: string) => {
    this.selectedCategory = categoryId;
  };

  get skillsInsightLabel() {
    if (this.isDemoMode) {
      const clientId = this.demoClient.client.id;

      if (clientId === 'starbucks') {
        return 'Customer experience skills vs. peers';
      }

      if (clientId === 'ups') {
        return 'Operations skills vs. peers';
      }

      return 'Patient care skills vs. peers';
    }

    return 'Skills benchmarks across your organization';
  }

  get trendingSkills() {
    if (this.isDemoMode) {
      const clientId = this.demoClient.client.id;

      if (clientId === 'starbucks') {
        return [
          { name: 'Customer Experience', growth: '+42%' },
          { name: 'Leadership', growth: '+35%' },
          { name: 'Inventory Management', growth: '+28%' },
        ];
      }

      if (clientId === 'ups') {
        return [
          { name: 'Safety Compliance', growth: '+38%' },
          { name: 'Leadership', growth: '+33%' },
          { name: 'Process Improvement', growth: '+27%' },
        ];
      }

      return [
        { name: 'Epic EMR', growth: '+45%' },
        { name: 'Leadership', growth: '+38%' },
        { name: 'Quality Improvement', growth: '+32%' },
      ];
    }

    const topSkills = [...this.skills]
      .sort((a, b) => (b.proficiencyScore ?? 0) - (a.proficiencyScore ?? 0))
      .slice(0, 3);

    if (!topSkills.length) {
      return [
        { name: 'Leadership', growth: '+24%' },
        { name: 'Communication', growth: '+18%' },
        { name: 'Problem Solving', growth: '+15%' },
      ];
    }

    return topSkills.map((skill) => ({
      name: skill.name,
      growth: `+${Math.max(12, Math.round((skill.proficiencyScore ?? 0) / 3))}%`,
    }));
  }

  toggleAddSkill = () => {
    this.isAddingSkill = !this.isAddingSkill;
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <DataIndicator @source={{this.dataSource}} @showBadge={{true}} @dataSourceId="skills">
      <VStack class="data-audit w-full min-w-0">
        {{! Skills Profile Header }}
        <PageHero @gradient="warm">
          <div class="flex justify-between items-start">
            <div>
              <div class="tio-row mb-2">
                <PhLightbulb @size={{32}} />
                <Heading @level={{1}} class="text-white">Your Skills Profile</Heading>
              </div>
              <Text class="text-amber-100 text-lg mb-4">
                {{this.summary.skillsTracked}}
                skills tracked •
                {{this.summary.skillsVerified}}
                verified •
                {{this.summary.skillsInProgress}}
                in progress
              </Text>
              <div class="flex gap-6">
                <div class="tio-row-tight">
                  <PhCheckCircle @size={{20}} />
                  <span>{{this.summary.skillsVerified}} Verified Skills</span>
                </div>
                <div class="tio-row-tight">
                  <PhTrendUp @size={{20}} />
                  <span>{{this.summary.skillsInProgress}} Learning Now</span>
                </div>
                <div class="tio-row-tight">
                  <PhTarget @size={{20}} />
                  <span>{{this.skillGaps.length}} Skill Gaps</span>
                </div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-5xl font-bold">{{this.summary.profileCompleteness}}%</div>
              <Text class="text-amber-100">Profile Completeness</Text>
              <Button
                @intent="default"
                @icon={{PhPlus}}
                @label="Add Skill"
                class="mt-3 bg-white/20 hover:bg-white/30 text-white border-white/30"
              />
            </div>
          </div>
        </PageHero>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {{! Main Content }}
          <div class="lg:col-span-2 space-y-6">
            {{! Skills Categories Tabs }}
            <div class="bg-white rounded-lg shadow-sm p-2">
              <FilterTabs
                @tabs={{this.categoryTabs}}
                @activeId={{this.selectedCategory}}
                @onSelect={{this.setCategory}}
                @activeClass="tio-bg-warning text-white"
                @inactiveClass="tio-fg-secondary hover:tio-bg-sunken"
                @countActiveClass="bg-white/20"
                @countInactiveClass="bg-gray-200 tio-fg-secondary"
                class="flex gap-2"
              />
            </div>

            {{! Verified Skills }}
            <Section class="bg-white rounded-lg shadow-sm">
              <:header>
                <div class="flex items-center justify-between">
                  <div class="tio-row-tight">
                    <PhCheckCircle @size={{20}} class="tio-fg-success" />
                    <span>Verified Skills</span>
                  </div>
                  <Chip @intent="success">{{this.verifiedSkills.length}} Skills</Chip>
                </div>
              </:header>
              <:body>
                <Text @variant="muted" @size="sm" class="mb-4">
                  These skills have been verified through certifications, courses, or work
                  experience.
                </Text>
                <div class="space-y-3">
                  {{#each this.verifiedSkills as |skill|}}
                    <div
                      class="flex items-center justify-between p-4 tio-bg-success-soft rounded-lg border tio-border-success"
                    >
                      <div class="tio-row-loose">
                        <div
                          class="w-12 h-12 rounded-full tio-bg-success text-white flex items-center justify-center"
                        >
                          <PhCheckCircle @size={{24}} />
                        </div>
                        <div>
                          <div class="font-semibold tio-fg-primary">{{skill.name}}</div>
                          <div class="tio-row-tight text-sm tio-fg-muted">
                            <span>{{this.formatSkillLevel skill.level}}</span>
                            <span>•</span>
                            <span>{{skill.verifiedBy}}</span>
                          </div>
                        </div>
                      </div>
                      <div class="tio-row">
                        <Chip @intent="success">Verified</Chip>
                        <Button @intent="default" @appearance="outlined" @size="sm">
                          <PhPencil @size={{16}} />
                        </Button>
                      </div>
                    </div>
                  {{/each}}
                </div>
              </:body>
            </Section>

            {{! Skills In Progress }}
            <Section class="bg-white rounded-lg shadow-sm">
              <:header>
                <div class="flex items-center justify-between">
                  <div class="tio-row-tight">
                    <PhGraduationCap @size={{20}} class="tio-fg-brand" />
                    <span>Skills In Progress</span>
                  </div>
                  <Chip @intent="primary">{{this.inProgressSkills.length}} Learning</Chip>
                </div>
              </:header>
              <:body>
                <Text @variant="muted" @size="sm" class="mb-4">
                  Skills you're currently developing through courses and training.
                </Text>
                <div class="space-y-4">
                  {{#each this.inProgressSkills as |skill|}}
                    <div class="p-4 tio-bg-brand-wash rounded-lg border tio-border-brand-soft">
                      <div class="flex items-center justify-between mb-3">
                        <div>
                          <div class="font-semibold tio-fg-primary">{{skill.name}}</div>
                          <Text @variant="muted" @size="sm">
                            Target completion:
                            {{this.formatDate skill.targetDate}}
                          </Text>
                        </div>
                        <div class="text-right">
                          <div class="font-bold tio-fg-brand">{{skill.progress}}%</div>
                          <Text @variant="muted" @size="xs">Complete</Text>
                        </div>
                      </div>
                      <ProgressBar @value={{skill.progress}} @color="blue" />
                      <Text @variant="muted" @size="sm" class="mt-2">
                        Est. completion:
                        {{this.formatDate skill.targetDate}}
                      </Text>
                    </div>
                  {{/each}}
                </div>
              </:body>
            </Section>

            {{! Self-Reported Skills }}
            <Section class="bg-white rounded-lg shadow-sm">
              <:header>
                <div class="flex items-center justify-between">
                  <div class="tio-row-tight">
                    <PhStar @size={{20}} class="tio-fg-warning" />
                    <span>Self-Reported Skills</span>
                  </div>
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @size="sm"
                    @icon={{PhPlus}}
                    @label="Add Skill"
                  />
                </div>
              </:header>
              <:body>
                <Text @variant="muted" @size="sm" class="mb-4">
                  Skills you've added to your profile. Consider getting them verified!
                </Text>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {{#each this.selfReportedSkills as |skill|}}
                    <div
                      class="flex items-center justify-between p-3 tio-bg-sunken rounded-lg border tio-border-default hover:tio-border-warning transition-colors"
                    >
                      <div>
                        <div class="font-medium tio-fg-primary">{{skill.name}}</div>
                        <Text @variant="muted" @size="sm">{{this.formatSkillLevel
                            skill.level
                          }}</Text>
                      </div>
                      <Button @intent="warning" @appearance="outlined" @size="sm">
                        Verify
                      </Button>
                    </div>
                  {{/each}}
                </div>
              </:body>
            </Section>
          </div>

          {{! Sidebar }}
          <div class="space-y-6">
            {{! Skill Gaps Alert }}
            <Section class="bg-white rounded-lg shadow-sm border-l-4 border-l-red-500">
              <:header>
                <div class="tio-row-tight">
                  <PhWarning @size={{20}} class="tio-fg-danger" />
                  <span>Skill Gaps</span>
                </div>
              </:header>
              <:body>
                <Text @variant="muted" @size="sm" class="mb-4">
                  Skills needed for your career goals that you haven't developed yet.
                </Text>
                <div class="space-y-3">
                  {{#each this.skillGaps as |gap|}}
                    <div
                      class="p-3 rounded-lg border cursor-pointer hover:shadow-md transition-all
                        {{if
                          (eq gap.importance 'Critical')
                          'tio-bg-danger-soft tio-border-danger'
                          (if
                            (eq gap.importance 'Important')
                            'tio-bg-warning-soft tio-border-warning'
                            'tio-bg-sunken tio-border-default'
                          )
                        }}"
                    >
                      <div class="flex items-start justify-between mb-2">
                        <div class="font-semibold tio-fg-primary">{{gap.name}}</div>
                        <Chip
                          @intent={{if
                            (eq gap.importance "Critical")
                            "danger"
                            (if (eq gap.importance "Important") "warning" "default")
                          }}
                        >
                          {{gap.importance}}
                        </Chip>
                      </div>
                      <Text @variant="muted" @size="sm">Required for: {{gap.targetRole}}</Text>
                      <div class="tio-row-tight mt-2 text-sm tio-fg-brand">
                        <PhSparkle @size={{14}} />
                        <span>{{gap.learningPaths.length}} learning options</span>
                        <PhArrowRight @size={{14}} />
                      </div>
                    </div>
                  {{/each}}
                </div>
                <LinkTo
                  @route="authenticated.reimagined.skills.gaps"
                  class="block w-full mt-4 text-center px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  Close These Gaps
                </LinkTo>
              </:body>
            </Section>

            {{! Skills Insights }}
            <Section class="bg-white rounded-lg shadow-sm">
              <:header>
                <div class="tio-row-tight">
                  <PhChartBar @size={{20}} class="tio-fg-brand" />
                  <span>Skills Insights</span>
                </div>
              </:header>
              <:body>
                <div class="space-y-4">
                  <StatCard
                    @icon={{PhStar}}
                    @label={{this.skillsInsightLabel}}
                    @value="Top 15%"
                    @color="violet"
                  />

                  <div class="border-t tio-border-default pt-4">
                    <Text @variant="muted" @size="sm" class="mb-3">Trending skills at your company:</Text>
                    <div class="space-y-2">
                      {{#each this.trendingSkills as |trend|}}
                        <div class="flex items-center justify-between">
                          <span class="text-sm">{{trend.name}}</span>
                          <div class="tio-row-extra-tight tio-fg-success text-sm">
                            <PhTrendUp @size={{14}} />
                            <span>{{trend.growth}}</span>
                          </div>
                        </div>
                      {{/each}}
                    </div>
                  </div>
                </div>
              </:body>
            </Section>

            {{! Quick Actions }}
            <Section class="bg-white rounded-lg shadow-sm">
              <:header>Quick Actions</:header>
              <:body>
                <div class="space-y-2">
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @icon={{PhCertificate}}
                    @label="Take Skills Assessment"
                    class="w-full justify-start"
                  />
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @icon={{PhBriefcase}}
                    @label="Import from LinkedIn"
                    class="w-full justify-start"
                  />
                  <Button
                    @intent="primary"
                    @appearance="outlined"
                    @icon={{PhGraduationCap}}
                    @label="Browse Courses"
                    class="w-full justify-start"
                  />
                </div>
              </:body>
            </Section>
          </div>
        </div>
      </VStack>
    </DataIndicator>
  </template>
}
